import { audiencerateApi } from '@/services/audiencerate';

/**
 * Geocoding state of one datasource, read by `cdp-app-api` from the tables written by
 * `mdp-background-tasks`. The nullable counts are null until the job has written a fresh
 * state row, so an absent `pending` can never be read as zero.
 */
export type GeoDatasourceState = {
  datasourceId: string;
  /** Distinct citizens (fiscal codes) of the datasource, address or not. */
  total: number;
  /** The datasource carries fiscal codes and addresses (by fresh counts, or by CSV mapping before the first sweep). */
  qualifies: boolean;
  /** ACTIVE, fresh state row, some located citizens and pending within the residue. */
  ready: boolean;
  polygonEligible: number | null;
  streetOnly: number | null;
  pending: number | null;
  unresolved: number | null;
  lastSweepAt: string | null;
  /** Bounding box of the citizens the polygon can match; null without a fresh row or without any. */
  bounds: GeoBounds | null;
};

export type GeoBounds = { latMin: number; latMax: number; lonMin: number; lonMax: number };

export type GeoStreet = { city: string; street: string; citizens: number };

export type MapsToken = { token: string; expiresOn: string; clientId: string };

type WorkspaceArg = { workspaceId: string };
type DatasourcesArg = WorkspaceArg & { datasourceIds: string[] };

export const geoApi = audiencerateApi.injectEndpoints({
  endpoints: (build) => ({
    getGeoDatasourceStates: build.query<GeoDatasourceState[], DatasourcesArg>({
      query: ({ workspaceId, datasourceIds }) => ({
        url: `/v1/workspaces/${workspaceId}/geo/datasources`,
        params: { ids: datasourceIds.join(',') },
      }),
    }),
    getGeoStreets: build.query<GeoStreet[], DatasourcesArg>({
      query: ({ workspaceId, datasourceIds }) => ({
        url: `/v1/workspaces/${workspaceId}/geo/streets`,
        params: { datasourceIds: datasourceIds.join(',') },
      }),
    }),
    getMapsToken: build.query<MapsToken, WorkspaceArg>({
      query: ({ workspaceId }) => ({ url: `/v1/workspaces/${workspaceId}/geo/maps-token` }),
      // the API caches the token until a minute before expiry; never serve a stale one from the store
      keepUnusedDataFor: 0,
    }),
  }),
});

export const { useGetGeoDatasourceStatesQuery, useGetGeoStreetsQuery, useLazyGetMapsTokenQuery } = geoApi;
